import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Award, Star, Trophy, Target } from 'lucide-react';

const EnhancedRankingDisplay = ({ college, layout = 'compact', academicFocus = null }) => {
  const [showAllRankings, setShowAllRankings] = useState(false);

  // Extract and organize all rankings
  const rankings = useMemo(() => {
    if (!college) return [];

    const rankingData = [
      // Note: Overall USNWR ranking now shown in main stats, so we exclude it here
      {
        type: 'engineering',
        field: 'engineering_ranking',
        value: college.engineering_ranking,
        label: 'Engineering Programs',
        shortLabel: 'Engineering',
        color: 'orange',
        isPrimary: academicFocus === 'engineering'
      },
      {
        type: 'nursing',
        field: 'nursing_ranking',
        value: college.nursing_ranking,
        label: 'Nursing Programs',
        shortLabel: 'Nursing',
        color: 'emerald',
        isPrimary: academicFocus === 'nursing'
      },
      {
        type: 'business',
        field: 'business_ranking',
        value: college.business_ranking,
        label: 'Business Programs',
        shortLabel: 'Business',
        color: 'violet',
        isPrimary: academicFocus === 'business'
      },
      {
        type: 'computer_science',
        field: 'computer_science_ranking',
        value: college.computer_science_ranking,
        label: 'Computer Science Programs',
        shortLabel: 'Computer Science',
        color: 'indigo',
        isPrimary: academicFocus === 'computer_science' || academicFocus === 'cs'
      }
    ].filter(ranking => ranking.value); // Only include rankings that have values

    // Sort by priority: primary rankings first, then by ranking value
    const sortedRankings = rankingData.sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      return a.value - b.value; // Lower ranking number = better rank
    });
    
    return sortedRankings;
  }, [college, academicFocus]);

  const primaryRanking = rankings.find(r => r.isPrimary) || rankings[0];
  const secondaryRankings = rankings.filter(r => r !== primaryRanking);

  const getColorClasses = (color, variant = 'default') => {
    const colorMap = {
      azure: {
        default: 'bg-azure-100 text-azure-800 border-azure-200',
        badge: 'bg-azure-50 text-azure-700 border-azure-200',
        hero: 'bg-azure-600 text-white',
        accent: 'text-azure-600 bg-azure-50 border-azure-200'
      },
      orange: {
        default: 'bg-orange-100 text-orange-800 border-orange-200',
        badge: 'bg-orange-50 text-orange-700 border-orange-200',
        hero: 'bg-orange-600 text-white',
        accent: 'text-orange-600 bg-orange-50 border-orange-200'
      },
      emerald: {
        default: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        hero: 'bg-emerald-600 text-white',
        accent: 'text-emerald-600 bg-emerald-50 border-emerald-200'
      },
      violet: {
        default: 'bg-violet-100 text-violet-800 border-violet-200',
        badge: 'bg-violet-50 text-violet-700 border-violet-200',
        hero: 'bg-violet-600 text-white',
        accent: 'text-violet-600 bg-violet-50 border-violet-200'
      },
      indigo: {
        default: 'bg-indigo-100 text-indigo-800 border-indigo-200',
        badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        hero: 'bg-indigo-600 text-white',
        accent: 'text-indigo-600 bg-indigo-50 border-indigo-200'
      }
    };
    return colorMap[color]?.[variant] || colorMap.azure[variant];
  };

  const getRankingIcon = (type) => {
    switch (type) {
      case 'overall': return Trophy;
      case 'engineering': return Target;
      case 'nursing': return Star;
      case 'business': return Award;
      case 'computer_science': return Target;
      default: return Star;
    }
  };

  const formatRankingValue = (value, type) => {
    if (!value) return null;
    
    // Handle special values
    const valueStr = value.toString().toUpperCase();
    if (valueStr.includes('UNWR') || valueStr === 'UNRANKED') {
      return 'Unranked';
    }
    
    return `#${value}`;
  };

  if (!rankings.length) {
    return (
      <div className="text-center py-4 text-stone-500">
        <Star className="w-6 h-6 mx-auto mb-2 text-stone-400" />
        <p className="text-sm">No rankings available</p>
      </div>
    );
  }

  // Compact layout for cards/inline display
  if (layout === 'compact') {
    return (
      <div className="flex flex-wrap gap-2">
        {rankings.slice(0, showAllRankings ? rankings.length : 3).map((ranking) => {
          const IconComponent = getRankingIcon(ranking.type);
          const rankingValue = formatRankingValue(ranking.value, ranking.type);
          
          return (
            <div
              key={ranking.type}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${getColorClasses(ranking.color, 'badge')}`}
            >
              <IconComponent className="w-3 h-3" />
              <span>{ranking.shortLabel} {rankingValue}</span>
            </div>
          );
        })}
        
        {rankings.length > 3 && (
          <button
            onClick={() => setShowAllRankings(!showAllRankings)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 border border-stone-200 rounded-full hover:bg-stone-50 transition-colors"
          >
            {showAllRankings ? (
              <>
                <ChevronUp className="w-3 h-3" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3" />
                +{rankings.length - 3} more
              </>
            )}
          </button>
        )}
      </div>
    );
  }

  // Detailed layout for full displays
  if (layout === 'detailed') {
    return (
      <div className="space-y-4">
        {/* Primary Ranking - Hero Display */}
        {primaryRanking && (
          <div className={`rounded-lg p-6 border-2 ${getColorClasses(primaryRanking.color, 'accent')}`}>
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${getColorClasses(primaryRanking.color, 'hero')}`}>
                {React.createElement(getRankingIcon(primaryRanking.type), { className: "w-6 h-6" })}
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {formatRankingValue(primaryRanking.value, primaryRanking.type)}
                </div>
                <div className="text-sm font-medium">{primaryRanking.label}</div>
                {primaryRanking.isPrimary && (
                  <div className="text-xs text-slate-600 mt-1">Primary ranking</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Secondary Rankings Grid */}
        {secondaryRankings.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {secondaryRankings.map((ranking) => {
              const IconComponent = getRankingIcon(ranking.type);
              const rankingValue = formatRankingValue(ranking.value, ranking.type);
              
              return (
                <div
                  key={ranking.type}
                  className={`flex items-center gap-3 p-4 rounded-lg border ${getColorClasses(ranking.color, 'badge')}`}
                >
                  <IconComponent className="w-5 h-5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{rankingValue}</div>
                    <div className="text-xs truncate">{ranking.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Hero layout for main displays
  if (layout === 'hero' && primaryRanking) {
    const IconComponent = getRankingIcon(primaryRanking.type);
    const rankingValue = formatRankingValue(primaryRanking.value, primaryRanking.type);
    
    return (
      <div className={`text-center p-8 rounded-xl ${getColorClasses(primaryRanking.color, 'accent')}`}>
        <div className={`inline-flex p-4 rounded-full mb-4 ${getColorClasses(primaryRanking.color, 'hero')}`}>
          <IconComponent className="w-8 h-8" />
        </div>
        <div className="text-4xl font-bold mb-2">{rankingValue}</div>
        <div className="text-lg font-medium mb-1">{primaryRanking.label}</div>
        
        {secondaryRankings.length > 0 && (
          <div className="mt-4 pt-4 border-t border-current opacity-60">
            <div className="flex flex-wrap justify-center gap-2">
              {secondaryRankings.slice(0, 2).map((ranking) => (
                <span key={ranking.type} className="text-xs font-medium">
                  {ranking.shortLabel} {formatRankingValue(ranking.value, ranking.type)}
                </span>
              ))}
              {secondaryRankings.length > 2 && (
                <span className="text-xs">+{secondaryRankings.length - 2} more</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default fallback
  return (
    <div className="space-y-2">
      {rankings.map((ranking) => {
        const IconComponent = getRankingIcon(ranking.type);
        const rankingValue = formatRankingValue(ranking.value, ranking.type);
        
        return (
          <div
            key={ranking.type}
            className={`flex items-center justify-between p-3 rounded-lg ${getColorClasses(ranking.color, 'badge')}`}
          >
            <div className="flex items-center gap-2">
              <IconComponent className="w-4 h-4" />
              <span className="text-sm font-medium">{ranking.shortLabel}</span>
            </div>
            <span className="font-semibold">{rankingValue}</span>
          </div>
        );
      })}
    </div>
  );
};

export default EnhancedRankingDisplay;